"use client"

import { ColumnDef } from "@tanstack/react-table"
import { MoreHorizontal } from "lucide-react"
import { Checkbox } from "@/Components/ui/checkbox"
import { Button } from "@/Components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/Components/ui/dropdown-menu"

// This type is used to define the shape of our data.
// You can use a Zod schema here if you want.
export type User = {
    id: string | null
    forename: string
    surname: string
    userName: string
    displayName: string
    email: string
    role: "Admin" | "User"
}

export const columns = (HandleSetRoleClicked: (id:string, role: "User" | "Admin") => void, 
HandleDeleteClicked:(id:string) => void, 
HandleEditClicked:(id:string)=>void,
HandleResetPasswordClicked:(userId:string)=>void): ColumnDef<User>[] => [
    {
        id: "select",
        header: ({ table }) => (
            <Checkbox
                checked={
                    table.getIsAllPageRowsSelected() ||
                    (table.getIsSomePageRowsSelected() && "indeterminate")
                }
                onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                aria-label="Select all"
            />
        ),
        cell: ({ row }) => (
            <div className="text-center">
            <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => row.toggleSelected(!!value)}
                aria-label="Select row"
            />
            </div>
        ),
        enableSorting: false,
        enableHiding: false,
    },
    {
        id: "id",
        accessorKey: "id",
        header: "ID",
        cell: ({ row }) => {
            const id = row.getValue("id");
            return <div className="text-center">{ id as string }</div>
        },
    },
    {
        id: "forename",
        accessorKey: "forename",
        header: "Forename",
        cell: ({ row }) => {
            const forename = row.getValue("forename");
            return <div className="text-center">{ forename as string }</div>
        },
        size: 200,
    },
    {
        id: "surname",
        accessorKey: "surname",
        header: "Surname",
        cell: ({ row }) => {
            const surname = row.getValue("surname");
            return <div className="text-center">{ surname as string }</div>
        },
        size: 200,
    },
    {
        id: "displayName",
        accessorKey: "displayName",
        header: "Display Name",
        cell: ({ row }) => {
            const displayName = row.getValue("displayName");
            return <div className="text-center">{displayName as string}</div>
        },
        size: 200,
    },
    {
        id: "email",
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => {
            const email = row.getValue("email");
            return <div className="text-center">{email as string}</div>
        },
        size: 200,
    },
    {
        id: "role",
        accessorKey: "role",
        header: "User Role",
        cell: ({ row }) => {
            const role = row.getValue("role");
            return <div className="text-center">{role as string}</div>
        },
        size: 200,
    },
    {
        id: "actions",
        cell: ({ row }) => {

            return (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem
                            onClick={() => HandleEditClicked(row.getValue("id"))}
                        >
                            Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => HandleSetRoleClicked(row.getValue("id"), "Admin")}
                        >
                            Set Role to Admin
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => HandleSetRoleClicked(row.getValue("id"), "User")}
                        >
                            Set Role to User
                        </DropdownMenuItem>
                        <DropdownMenuItem
                             onClick={() => HandleResetPasswordClicked(row.getValue("id"))}
                        >
                            Send Password Reset Email
                        </DropdownMenuItem>
                        <DropdownMenuItem
                             onClick={() => HandleDeleteClicked(row.getValue("id"))}
                        >
                            Delete
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        },
    },
]

export const columnDisplayNames: [string, string][] = [
    ["id", "ID"],
    ["forename", "Forename"],
    ["surname", "Surname"],
    ["displayName", "Display Name"],
    ["email", "Email"],
    ["role", "User Role"],
]
